import React, { useState, useEffect, useCallback } from 'react';
import { getAdminPassword, getAdminHeader } from '../shared/authConfig';
import { addDaysLondon, formatLondonDateTime, londonDateKey } from '../shared/dateUtils';
import './SlotManagement.css';

interface SlotOrderSummary {
    orderId: string;
    customerName: string;
    address: string;
    total: number;
    status: string;
    orderDate: string;
    itemCount: number;
}

interface SlotAvailability {
    time: string;
    orderCount: number;
    maxOrders: number;
    isAvailable: boolean;
    remainingCapacity: number;
    orders?: SlotOrderSummary[];
}

interface SlotsResponse {
    date: string;
    maxOrdersPerSlot: number;
    slots: SlotAvailability[];
}

function getAdminHeaders(): Record<string, string> {
    const password = getAdminPassword();
    if (password) {
        return { [getAdminHeader()]: password };
    }
    return {};
}

const SlotManagement: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<string>(() => londonDateKey());
    const [slotsData, setSlotsData] = useState<SlotsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());

    const fetchSlots = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `/api/slots/${selectedDate}?includeOrders=true`,
                { headers: getAdminHeaders() }
            );
            if (!response.ok) {
                throw new Error('Failed to fetch slots');
            }
            const data = await response.json() as SlotsResponse;
            setSlotsData(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load slots');
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchSlots();
    }, [fetchSlots]);

    const handleRemoveOrder = async (time: string, orderId: string) => {
        if (!confirm(`Remove order ${orderId} from slot ${time}?`)) {
            return;
        }

        try {
            const encodedTime = encodeURIComponent(time);
            const response = await fetch(
                `/api/slots/${selectedDate}/${encodedTime}/${orderId}`,
                {
                    method: 'DELETE',
                    headers: getAdminHeaders()
                }
            );

            if (!response.ok) {
                const errorData = await response.json() as { error?: string };
                throw new Error(errorData.error || 'Failed to remove order');
            }

            // Refresh data
            await fetchSlots();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to remove order');
        }
    };

    const toggleSlotExpanded = (time: string) => {
        setExpandedSlots(prev => {
            const newSet = new Set(prev);
            if (newSet.has(time)) {
                newSet.delete(time);
            } else {
                newSet.add(time);
            }
            return newSet;
        });
    };

    const formatTime = (time: string): string => {
        return time;
    };

    const formatDate = (dateStr: string): string => {
        // `dateStr` is an ISO timestamp (UTC); display in Europe/London.
        return formatLondonDateTime(dateStr, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDateOptions = (): string[] => {
        const dates: string[] = [];
        const todayKey = londonDateKey();
        for (let i = -7; i <= 14; i++) {
            dates.push(addDaysLondon(todayKey, i));
        }
        return dates;
    };

    const getDateLabel = (dateStr: string): string => {
        const todayKey = londonDateKey();
        const tomorrowKey = addDaysLondon(todayKey, 1);

        if (dateStr === todayKey) return `Today (${dateStr})`;
        if (dateStr === tomorrowKey) return `Tomorrow (${dateStr})`;

        // Anchor at noon UTC to avoid the London date drifting across DST.
        return formatLondonDateTime(`${dateStr}T12:00:00Z`, { weekday: 'short', day: 'numeric', month: 'short' });
    };

    const totalOrders = slotsData?.slots.reduce((sum, slot) => sum + slot.orderCount, 0) || 0;

    return (
        <div className="slot-management">
            <div className="slot-management-header">
                <h2>📅 Time Slot Management</h2>
                <p>View and manage delivery time slots</p>
            </div>

            <div className="slot-controls">
                <div className="date-selector">
                    <label htmlFor="slot-date">Select Date:</label>
                    <select
                        id="slot-date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    >
                        {getDateOptions().map(date => (
                            <option key={date} value={date}>
                                {getDateLabel(date)}
                            </option>
                        ))}
                    </select>
                </div>
                <button className="refresh-btn" onClick={fetchSlots} disabled={loading}>
                    🔄 Refresh
                </button>
            </div>

            {loading && (
                <div className="slot-loading">Loading slots...</div>
            )}

            {error && (
                <div className="slot-error">
                    <p>❌ {error}</p>
                    <button onClick={fetchSlots}>Retry</button>
                </div>
            )}

            {!loading && !error && slotsData && (
                <>
                    <div className="slot-summary">
                        <div className="summary-card">
                            <span className="summary-label">Date</span>
                            <span className="summary-value">{slotsData.date}</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Total Orders</span>
                            <span className="summary-value">{totalOrders}</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Active Slots</span>
                            <span className="summary-value">{slotsData.slots.length}</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Max per Slot</span>
                            <span className="summary-value">{slotsData.maxOrdersPerSlot}</span>
                        </div>
                    </div>

                    {slotsData.slots.length === 0 ? (
                        <div className="no-slots">
                            <p>No orders scheduled for this date.</p>
                        </div>
                    ) : (
                        <div className="slots-list">
                            {slotsData.slots.map(slot => (
                                <div key={slot.time} className="slot-card">
                                    <div
                                        className="slot-header"
                                        onClick={() => toggleSlotExpanded(slot.time)}
                                    >
                                        <div className="slot-time">
                                            <span className="time-value">{formatTime(slot.time)}</span>
                                            <span className={`slot-status ${slot.isAvailable ? 'available' : 'full'}`}>
                                                {slot.isAvailable ? 'Available' : 'Full'}
                                            </span>
                                        </div>
                                        <div className="slot-count">
                                            <span className="order-count">
                                                {slot.orderCount} / {slot.maxOrders} orders
                                            </span>
                                            <span className="expand-icon">
                                                {expandedSlots.has(slot.time) ? '▼' : '▶'}
                                            </span>
                                        </div>
                                    </div>

                                    {expandedSlots.has(slot.time) && slot.orders && (
                                        <div className="slot-orders">
                                            {slot.orders.length === 0 ? (
                                                <p className="no-orders">No order details available</p>
                                            ) : (
                                                slot.orders.map(order => (
                                                    <div key={order.orderId} className="order-card">
                                                        <div className="order-main">
                                                            <div className="order-id">
                                                                #{order.orderId.slice(-6).toUpperCase()}
                                                            </div>
                                                            <div className="order-details">
                                                                <p className="customer-name">
                                                                    {order.customerName || 'No name'}
                                                                </p>
                                                                <p className="address">{order.address}</p>
                                                                <p className="order-meta">
                                                                    <span className="items">{order.itemCount} items</span>
                                                                    <span className="total">£{order.total.toFixed(2)}</span>
                                                                    <span className={`status status-${order.status}`}>
                                                                        {order.status}
                                                                    </span>
                                                                </p>
                                                                <p className="order-date">
                                                                    Ordered: {formatDate(order.orderDate)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="order-actions">
                                                            <button
                                                                className="remove-btn"
                                                                onClick={() => handleRemoveOrder(slot.time, order.orderId)}
                                                                title="Remove from slot"
                                                            >
                                                                🗑️ Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SlotManagement;
